import { z } from 'zod'

import { RequestOverrides } from '@/clients/BaseApiClient'
import { HTTPMethod } from '@/lib/requests/types'
import { CreditCardId, CreditCardResponse, CreditCardZ, GetCreditCardStatusResponseZ } from '@/types/creditCard'
import { TripMockMetadataZ } from '@/types/mock'

import { getGGClient } from './GetGoingClient'

const RequestCreditCardUnmaskResponseZ = z.object({
	request_id: z.string(),
	expires_in_seconds: z.number(),
})

const ConfirmCreditCardUnmaskResponseZ = z.object({
	encrypted_card_number: z.string(),
	salt: z.string(),
	nonce: z.string(),
	display_duration_seconds: z.number(),
})

export async function deleteCreditCard(creditCardId: CreditCardId, overrides?: RequestOverrides) {
	await getGGClient().request('DELETE', `credit_card/${creditCardId}`, overrides)
}

export async function getCreditCard(creditCardId: CreditCardId, overrides?: RequestOverrides) {
	return getGGClient()
		.request('GET', `credit_card/${creditCardId}`, {
			responseSchema: CreditCardZ,
			...overrides,
		})
		.then((r) => r.data as CreditCardResponse)
}

getCreditCard.queryKey = (creditCardId: CreditCardId) => ['creditCard', { creditCardId }] as const

export async function getCreditCardStatus(creditCardId: CreditCardId, overrides?: RequestOverrides) {
	return getGGClient()
		.request('GET', `credit_card/${creditCardId}/status`, {
			responseSchema: GetCreditCardStatusResponseZ,
			...overrides,
		})
		.then((r) => r.data)
}

export async function getCreditCardMockOptions(overrides?: RequestOverrides) {
	return getGGClient()
		.request('GET', `trips/mock/metadata`, {
			responseSchema: TripMockMetadataZ,
			...overrides,
		})
		.then((r) =>
			Object.fromEntries(
				Object.entries(r.data.mock_options).filter(([key]) =>
					['airplus_', 'tokenizer_', 'worldline_'].some((prefix) => key.startsWith(prefix)),
				),
			),
		)
}

getCreditCardMockOptions.queryKey = ['creditCard', 'mocks', 'metadata'] as const

export async function setCreditCardMock(creditCardId: CreditCardId, mock: string | null, overrides?: RequestOverrides) {
	await getGGClient().request('POST', `credit_card/${creditCardId}/mock`, {
		requestSchema: z.object({ mock: z.string().nullable() }),
		data: { mock },
		...overrides,
	})
}

export async function requestCreditCardUnmask(
	creditCardId: CreditCardId,
	justification: string,
	overrides?: RequestOverrides,
) {
	return getGGClient()
		.request(HTTPMethod.POST, `credit_card/${creditCardId}/unmask/request`, {
			requestSchema: z.object({
				justification: z.string().min(10),
			}),
			responseSchema: RequestCreditCardUnmaskResponseZ,
			data: { justification },
			...overrides,
		})
		.then((r) => r.data)
}

export async function confirmCreditCardUnmask(
	creditCardId: CreditCardId,
	requestId: string,
	otp: string,
	overrides?: RequestOverrides,
) {
	return getGGClient()
		.request(HTTPMethod.POST, `credit_card/${creditCardId}/unmask/confirm`, {
			requestSchema: z.object({
				request_id: z.string(),
				otp: z.string().regex(/^\d{6}$/),
			}),
			responseSchema: ConfirmCreditCardUnmaskResponseZ,
			data: { request_id: requestId, otp },
			...overrides,
		})
		.then((r) => r.data)
}
